import type { MessageDescriptor } from 'react-intl-next';

import type { UiProductType, WorkspaceBundle } from '@adminhub/graphql-types';

import type { TupleUnion } from './common';
import type {
  AddProduct,
  CcpWacUrlPathArgs,
  LinksSchema,
  OfferingEnvironment,
  OfferingsSchemaWithIds,
  ProductConfigJson,
  WacUrlPathArgs,
} from './product-config-schema';

/**
 * This interface should be use to retrieve product metadata (i.e. name, id, key, etc.).
 * For now, this should be backwards compatible with existing {@link ProductKeyConverter}
 */
export interface ProductConfig {
  readonly type: UiProductType;
  /**
   * Human readable product name.
   * This is used in the UI as is and isn't localized.
   */
  readonly productName: string;

  /**
   * The localized name of the product, used for display in the UI.
   * This should be a translatable message descriptor to localize for internationalization support.
   */
  readonly localizedProductName: ProductConfigJson['localizedProductName'];

  /**
   * See https://stash.atlassian.com/projects/UN/repos/user-management/browse/hipster-user-management/src/main/java/com/atlassian/usermanagement/nextgen/model/product/ProductId.java
   *
   * Returns the `productId` that is used by `user-management` service.
   */
  readonly productId: string;

  /**
   * See https://workbench.staging.atl-paas.net/products?categoriesIn=CLOUD&billingSystem=CCP&productOwner=Atlassian
   * For now, in order to support backwards compatibility, CCP's productId is named `ccpProductId` whereas UM's productId is named `productId`. This will be corrected in the future.
   *
   * Returns the `productId` that is used by CCP and `admin-portfolio` services.
   */
  readonly ccpProductId: string | undefined;

  /**
   * See https://stash.atlassian.com/projects/IPS/repos/id-org-manager/browse/src/main/java/com/atlassian/orgmanager/services/products/ProductsData.java
   *
   * Returns the `productKey` that is used by `id-org-manager` service.
   */
  readonly key: string;
  /**
   * See https://bitbucket.org/atlassian/user-management/src/master/user-management-util/src/main/java/com/atlassian/usermanagement/productconfig/products/CanonicalProduct.java
   *
   * Returns the `canonicalProductKey` that is used by `user-management` service.
   */
  readonly canonicalProductKey: string;

  /**
   * See https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/
   *
   * Returns the resourceOwner portion of the ARI (i.e. `ari:cloud:<resourceOwner>::<resourceType>/<id>`)
   */
  readonly ariKey: string;

  /**
   * See https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/
   *
   * Returns the resourceType portion of the ARI (i.e. `ari:cloud:<resourceOwner>::<resourceType>/<id>`)
   */
  readonly ariResourceType: string;

  /**
   * These product keys are primarily used for xflow/product activation analytics
   *
   * When adding new keys, refer to https://bitbucket.org/atlassian/growth-analytics-pipeline-airflow/src/master/configurations/business/dim_model/subdag_configurations/dimensional_model/task_scripts/sql_scripts/07_dim_product.hql
   * Returns the emau product key, one of the standard product key sets used by xflow
   */
  readonly emauKey: string;
  readonly hamsProductKey: string | undefined;

  /**
   * See https://bitbucket.org/atlassian/sandbox-service/src/master/common/src/main/kotlin/com/atlassian/sandbox/model/SandboxableProductKey.kt
   *
   * Returns the `sandboxProductKey` that is used by `sandbox-service`.
   */
  readonly sandboxProductKey: string;

  /**
   * Returns the `enterpriseProductKey` that is used to determine if an organization has the enterprise edition of this product.`.
   */
  readonly enterpriseProductKey: string;

  /**
   * @deprecated Use {@link fromCollectionConfig} to determine if something is a collection
   */
  readonly isUiProductType: boolean;

  /**
   * @deprecated Use {@link type} to get the UiProductType
   */
  readonly uiProductType?: UiProductType;

  /**
   * Return the WorkspaceBundle if the product id is a bundle or UiProductType if the product it's just a product. Defaults to undefined.
   *
   * See https://workbench.staging.atl-paas.net/products?categoriesIn=CLOUD&billingSystem=CCP&productOwner=Atlassian
   *
   * TODO: This needs to be refactored. We should not be using union of types here.
   *
   * @deprecated Use {@link fromCollectionConfig} instead
   */
  readonly getBundleOrProductType: UiProductType | WorkspaceBundle | undefined;

  /**
   * The logo component name to be used for this product in the UI. This should be the name of the React component that can be rendered by the `@atlaskit/logo` component. Due to the React component requiring CSS to be pre-compiled, but not every usage of the config requires the CSS, the logo will be loaded via `@adminhub/product-logo`. This is optional and will default to the product name if not provided.
   */
  readonly productLogo: ProductConfigJson['productLogo'];

  /**
   * The icon component or SVG icon asset name to be used for this product in the UI. This should be either the name of the React component that can be rendered by the `@atlaskit/logo` component or a 3P icon from `@adminhub/images`. Due to the React component requiring CSS to be pre-compiled, but not every usage of the config requires the CSS, the icon will be loaded via `@adminhub/product-logo` or `@adminhub/images`.
   */
  readonly productIcon: ProductConfigJson['productIcon'];

  /**
   * The icon component name to be used for this product in the UI for Data Center products. This should be the name of the React component that can be rendered by the `@atlaskit/logo` component. Due to the React component requiring CSS to be pre-compiled, but not every usage of the config requires the CSS, the logo will be loaded via `@adminhub/product-logo`. This is optional and will default to the product name if not provided.
   */
  readonly dcProductIcon: ProductConfigJson['dcProductIcon'];

  /**
   * The display name for the settings link on the site menu. Defaults to the product name.
   */
  readonly productSettingName: ProductConfigJson['productSettingName'];

  /**
   * Will replace product specific links.
   *
   * Returns an object of links used in the UI that correspond to a particular product.
   */
  readonly links: LinksSchema;
  /**
   *
   * Returns true if product has links, false if empty.
   */
  hasLinks(): boolean;

  /**
   * The product sign-up link. Defaults to https://www.atlassian.com/try
   */
  readonly signUpLink: string;

  /**
   * Will replace add product functionality.
   * Returns an object with the data and messages needed for add product.
   */
  readonly addProduct: AddProduct;

  /**
   * A localized description of the product that provides detailed information about its purpose and functionality.
   * This is typically used in product introduction screens, help text, or detailed product information views.
   */
  readonly description: MessageDescriptor;

  /**
   * An array of localized summary points that highlight key features or benefits of the product.
   * These are typically displayed as bullet points in product overview sections or feature lists.
   */
  readonly summaryPoints: MessageDescriptor[];

  /**
   * The product offerings.  This should be added if the product needs offerings at https://workbench.staging.atl-paas.net/offerings/<offeringId>.
   */
  readonly offerings: OfferingsSchemaWithIds | {};

  /**
   * Returns the enterprise product offering.
   * Environment is not included for commercial offerings.
   */
  getEnterpriseOffering(environment?: OfferingEnvironment): OfferingsSchemaWithIds[string] | undefined;

  /**
   * Returns the offering for the child enterprise collection instance part of a parent enterprise collection
   * Environment is not included for commercial offerings.
   */
  getEnterpriseCollectionAppOffering(environment?: OfferingEnvironment): OfferingsSchemaWithIds[string] | undefined;

  /**
   * Returns the product offering.
   */
  getOffering(): OfferingsSchemaWithIds[string] | undefined;

  /**
   * Returns the wacUrlPath for the product.  The base WAC URL is defined in `@adminhub/config`.
   */
  wacUrlPath(args: Omit<WacUrlPathArgs, 'productKey'>): string | undefined;

  /**
   * Returns the ccpWacUrlPath for the product.  The base WAC URL is defined in `@adminhub/config`.
   */
  ccpWacUrlPath(args: CcpWacUrlPathArgs): string | undefined;
  // cont.
}

/**
 * This is the set of inputs that can be used to retrieve a {@link ProductConfig}
 */
export interface ProductConfigInputOptions {
  ariResourceOwner: string;
  productId: string;
  key: string;
  canonicalProductKey: string;
  type: UiProductType;
  /**
   * @deprecated Names should no longer be used as an input option for product config. Use {@link ariResourceOwner} instead.
   */
  name: string;
  billingProductKey: string;
  connectorType: string;
}

/**
 * The priority of which the keys from {@link ProductConfigInputOptions} should be used when creating a {@link ProductConfig}.
 */
export const ProductConfigInputOptionPriority: TupleUnion<keyof ProductConfigInputOptions> = [
  'ariResourceOwner',
  'type',
  'productId',
  'key',
  'canonicalProductKey',
  'name',
  'billingProductKey',
  'connectorType',
];

export type ProductName =
  | 'Bitbucket'
  | 'Jira'
  | 'Jira Products'
  | 'Jira Apps'
  | 'Jira Ops'
  | 'Jira Core'
  | 'Jira Work Management'
  | 'Jira Service Desk'
  | 'Jira Service Management'
  | 'Jira Product Discovery'
  | 'Jira Software'
  | 'Customer Service Management'
  | 'Opsgenie'
  | 'Trello'
  | 'Statuspage'
  | 'Stride'
  | 'Confluence'
  | 'Avocado'
  | 'Beacon'
  | 'Atlas'
  | 'Compass'
  | 'Fabric'
  | 'Atlassian Administration'
  | 'Atlassian Together'
  | 'Mercury'
  | 'Unified Help'
  | 'Loom'
  | 'Rovo'
  | 'Analytics'
  | 'Dev AI'
  | 'Radar'
  | 'Feedback'
  | 'Unknown Atlassian';
